import React from 'react';

import EditPenIcon from '@hero/ui-kit/icons/utility/EditPenIcon';
import TrashIcon from '@hero/ui-kit/icons/utility/TrashIcon';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import * as Style from './style.module.scss';

type TitleVariant = 'heading' | 'label';

const Title: React.FC<{ variant: TitleVariant; children: string }> = ({ variant, children }) =>
    variant === 'label' ? (
        <span className={Style.label}>{children}</span>
    ) : (
        <P size="small" strong noDefaultMargin>
            {children}
        </P>
    );

interface StickyNoteActionsProps {
    onCancel: () => void;
    /** Omit inside a `<form>`: Save then submits it. */
    onSave?: () => void;
    isSaving: boolean;
    cancelDisabled: boolean;
    saveDisabled: boolean;
    /** Shown above the buttons, e.g. a failed save. */
    errorMessage?: string;
}

/** The Cancel / Save row every sticky-note editor ends with. */
export const StickyNoteActions: React.FC<StickyNoteActionsProps> = ({
    onCancel,
    onSave,
    isSaving,
    cancelDisabled,
    saveDisabled,
    errorMessage
}) => (
    // Its own column, since a consumer's `<form>` sits between it and the card's flex gap.
    <div className={Style.footer}>
        {errorMessage ? (
            <P size="small" noDefaultMargin className={Style.error}>
                {errorMessage}
            </P>
        ) : null}
        <div className={Style.actions}>
            <Button
                variant="outlineSecondary"
                onClick={onCancel}
                disabled={cancelDisabled}
                size="small"
            >
                Cancel
            </Button>
            <Button
                type={onSave ? 'button' : 'submit'}
                onClick={onSave}
                disabled={saveDisabled}
                size="small"
            >
                {isSaving ? 'Saving…' : 'Save'}
            </Button>
        </div>
    </div>
);

interface StickyNoteEditorProps {
    name: string;
    titleVariant?: TitleVariant;
    draft: string;
    onDraftChange: (draft: string) => void;
    onCancel: () => void;
    onSave: () => void;
    canSave: boolean;
    isSaving: boolean;
    isPending: boolean;
    placeholder: string;
    errorMessage?: string;
}

/** A single-textarea editor, for notes that are just text. */
export const StickyNoteEditor: React.FC<StickyNoteEditorProps> = ({
    name,
    titleVariant = 'heading',
    draft,
    onDraftChange,
    onCancel,
    onSave,
    canSave,
    isSaving,
    isPending,
    placeholder,
    errorMessage
}) => (
    <>
        <Title variant={titleVariant}>{name}</Title>
        <textarea
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            // During IME composition Enter commits the candidate word, so
            // submitting there would swallow the keystroke. Shift+Enter is
            // the only way left to add a newline.
            onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                e.preventDefault();
                onSave();
            }}
            autoFocus
            className={Style.textarea}
            placeholder={placeholder}
        />
        <StickyNoteActions
            onCancel={onCancel}
            onSave={onSave}
            isSaving={isSaving}
            cancelDisabled={isPending}
            saveDisabled={!canSave}
            errorMessage={errorMessage}
        />
    </>
);

interface StickyNoteProps {
    /** What the note is called, e.g. "Sticky note": used in the action labels. */
    name: string;
    /** Header of the saved note, e.g. "Sticky note • Sep 15, 2026, 4:03 PM". */
    title: string;
    /** Saved text; empty renders the "add" prompt instead. */
    body: string | null;
    canEdit?: boolean;
    addLabel: string;
    /** `label` renders titles like a field label, with no divider under the header. */
    titleVariant?: TitleVariant;
    isEditing: boolean;
    onEdit: () => void;
    /** Replaces the note and `children` while editing. */
    editor: React.ReactNode;
    /** Omit to hide the delete icon. */
    onDelete?: () => void;
    /** Disables the edit and delete actions, e.g. while a delete is in flight. */
    isPending?: boolean;
    /** Extra content rendered below the note, inside the same card. */
    children?: React.ReactNode;
}

/** A single note card: header with edit (and optional delete), body, and an in-place editor. */
const StickyNote: React.FC<StickyNoteProps> = ({
    name,
    title,
    body,
    canEdit = true,
    addLabel,
    titleVariant = 'heading',
    isEditing,
    onEdit,
    editor,
    onDelete,
    isPending = false,
    children
}) => {
    if (isEditing) {
        return (
            <Section border noDefaultPadding className={`${Style.panel} ${Style.editing}`}>
                {editor}
            </Section>
        );
    }

    const editButton = (content: React.ReactNode, className: string, label?: string) => (
        <button
            type="button"
            className={className}
            onClick={onEdit}
            disabled={isPending}
            aria-label={label}
        >
            {content}
        </button>
    );

    const note = body ? (
        <>
            <div className={`${Style.header} ${titleVariant === 'label' ? Style.headerPlain : ''}`}>
                <Title variant={titleVariant}>{title}</Title>
                {canEdit ? (
                    <div className={Style.headerActions}>
                        {editButton(
                            <EditPenIcon type="utility" width={18} height={18} />,
                            Style.iconButton,
                            `Edit ${name.toLowerCase()}`
                        )}
                        {onDelete ? (
                            <button
                                type="button"
                                className={Style.iconButton}
                                onClick={onDelete}
                                disabled={isPending}
                                aria-label={`Delete ${name.toLowerCase()}`}
                            >
                                <TrashIcon type="utility" width={18} height={18} />
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </div>
            <P noDefaultMargin className={Style.body}>
                {body}
            </P>
        </>
    ) : canEdit ? (
        editButton(addLabel, Style.add)
    ) : null;

    return (
        <Section border noDefaultPadding className={Style.panel}>
            {note}
            {children}
        </Section>
    );
};

export default StickyNote;
